/**
 * @file PricingBadges.js
 * @version 1.6.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-11
 * @modified 2026-10-29
 *
 * 1.6.0 (2026-10-29): ContractGroupBadge — "Group: <name>" on a contract made
 * from a product group, and how many plain products the group has gained
 * since (Cam — "Plain product only, same pack").
 */

import React from 'react';
import { formatCurrency, formatPrice } from '../utils/currencyFormatter';
import { groupLabel } from '../utils/contractProductGroups';
import './ContractProductGroupPicker.css';

/**
 * Combined Cost / Price / Margin cell. Stacks the three figures, visually
 * distinguished, with margin shown as both an amount and a percentage of the
 * sell price (gross margin %). Negative margins flip to the error colour.
 */
export function PriceMarginCell({ price, cost, margin }) {
  const p = price != null && price !== '' ? parseFloat(price) : null;
  const c = cost != null && cost !== '' ? parseFloat(cost) : null;
  const m = margin != null && margin !== '' ? parseFloat(margin) : null;
  const pct = (m != null && p) ? (m / p) * 100 : null;
  return (
    <div className="price-margin-cell">
      <span className="pm-price" title="Sell price">{p != null ? formatPrice(p) : '-'}</span>
      <span className="pm-cost" title="Cost price">{(c != null && c !== 0) ? formatPrice(c) : '—'}</span>
      <span className={`pm-margin${m != null && m < 0 ? ' negative' : ''}`} title="Margin">
        {m != null ? formatCurrency(m) : '-'}
        {pct != null && <span className="pm-margin-pct"> ({pct.toFixed(2)}%)</span>}
      </span>
    </div>
  );
}

/**
 * Product cell shared by every contract-pricing table. Renders a flex row with
 * the product-code badge first (never wraps) and the item name in its own
 * block (0.9em / 1.3em). Optional additive code/name are appended.
 *
 * Props:
 *  - productCode, additiveCode: code strings (additive shown as "(CODE)")
 *  - productName, additiveName: name strings (additive shown as " + NAME")
 *  - isHolding: a holding product — no item yet; badged HOLDING
 */
export function ProductCell({ productCode, additiveCode, productName, additiveName, isHolding = false }) {
  const code = [productCode, additiveCode ? `(${additiveCode})` : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div className="product-cell">
      {code && <span className="product-code">{code}</span>}
      <div className="product-name">
        {productName || '-'}
        {additiveName && <span className="product-additive"> + {additiveName}</span>}
        {/* A contract agreed before its product exists: the name is the
            description, and the item is chosen later by editing it. */}
        {isHolding && (
          <span className="option-status-badge is-holding" title="Holding product — edit the contract to choose the item once it's set up">HOLDING</span>
        )}
      </div>
    </div>
  );
}

/**
 * Fulfilment status as a single coloured icon with an explanatory tooltip.
 * Shared by the Blends / Compounds / Straights pricing tables so the icon set
 * and wording stay identical everywhere.
 *
 * Props:
 *  - status: 'FULFILLED' | 'UTILISE' | 'UNFULFILLED' | null
 *  - manual: when true, render the "manually marked fulfilled" variant
 *            (overrides status).
 */
export function FulfilmentBadge({ status, manual = false }) {
  if (manual) {
    return (
      <span className="fulfilment-badge is-fulfilled" title="Manually marked as fulfilled — a user has flagged this contract complete, regardless of ordered quantity.">
        <span className="material-symbols-rounded">verified</span>
      </span>
    );
  }
  switch (status) {
    case 'FULFILLED':
      return (
        <span className="fulfilment-badge is-fulfilled" title="Fulfilled — the full contracted quantity has been ordered.">
          <span className="material-symbols-rounded">check_circle</span>
        </span>
      );
    case 'UTILISE':
      return (
        <span className="fulfilment-badge is-utilise" title="Utilise soon — quantity still remains and the contract period ends within 7 days.">
          <span className="material-symbols-rounded">error</span>
        </span>
      );
    case 'UNFULFILLED':
      return (
        <span className="fulfilment-badge is-unfulfilled" title="Unfulfilled — the contract period has ended with quantity still remaining.">
          <span className="material-symbols-rounded">cancel</span>
        </span>
      );
    default:
      return null;
  }
}

/**
 * Linked-PO indicator in the macOS-Mail style: a link icon with the count in a
 * small circular badge at the top-right. Click opens the linked-PO view.
 *
 * Props:
 *  - count: number of linked POs (renders nothing when 0/undefined)
 *  - onClick: click handler (stopPropagation already applied)
 *  - scope: optional label appended to the tooltip, e.g. "group total"
 */
export function LinkedPosBadge({ count, onClick, scope }) {
  const n = parseInt(count, 10) || 0;
  if (n <= 0) return null;
  const label = `${n} linked purchase order${n !== 1 ? 's' : ''}${scope ? ` (${scope})` : ''} — click to view`;
  return (
    <span
      className="linked-pos-badge"
      title={label}
      aria-label={label}
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); if (onClick) onClick(e); }}
    >
      <span className="material-symbols-rounded">link</span>
      <span className="linked-pos-count">{n > 99 ? '99+' : n}</span>
    </span>
  );
}

/**
 * Joint-account scope indicator for a contract / milk-powder / straights price.
 * Driven by the backend-computed `scopeKind` (pricing.contract_scope_kind):
 *  - 'none'          → nothing rendered (owner not in a joint-account group)
 *  - 'parent_only'   → link icon, --text-muted (applies to the parent only)
 *  - 'group'         → link icon, --color-primary (parent + child accounts)
 *  - 'children_only' → broken-link icon, --color-error (child account(s) only,
 *                      not the parent)
 */
export function ScopeLinkBadge({ scopeKind }) {
  const config = {
    parent_only: { icon: 'link', cls: '', label: 'This price applies to the parent account only' },
    group: { icon: 'link', cls: ' grouped', label: 'This price applies to the whole group (parent + child accounts)' },
    children_only: { icon: 'link_off', cls: ' children-only', label: 'This price applies to child account(s) only — not the parent' },
  };
  const c = config[scopeKind];
  if (!c) return null;
  return (
    <span className={`scope-link-badge${c.cls}`} title={c.label} aria-label={c.label}>
      <span className="material-symbols-rounded">{c.icon}</span>
    </span>
  );
}

/**
 * How a product is tied to a customer, as one icon per link source. Icons match
 * the Create Order product dropdown so the same thing reads the same way in
 * both places: handshake = contract, sell = spot price.
 *
 * Props:
 *  - links: [{ account_id, account_no, account_name, source, is_archived }]
 *  - max:   how many to render before collapsing to "+n" (default 3)
 */
export const LINK_SOURCES = {
  contract: { icon: 'handshake', label: 'contract' },
  spot: { icon: 'sell', label: 'spot price' },
  /* A person, not a blender. Every badge here means "priced for a customer" —
     the glyph only says by what mechanism — and a mixing machine read as a
     property of the PRODUCT rather than of who it belongs to. */
  blend: { icon: 'person', label: 'blend' },
  straights: { icon: 'grain', label: 'straights contract' },
  milk_powder: { icon: 'water_full', label: 'milk powder contract' },
  bags: { icon: 'shopping_bag', label: 'bag/tote contract' }
};

export function CustomerLinkBadges({ links, max = 3 }) {
  if (!Array.isArray(links) || links.length === 0) return null;

  // One icon per source, not per link: a customer with six compound contracts
  // on one product should read as a single handshake.
  const bySource = new Map();
  for (const l of links) {
    const entry = bySource.get(l.source) || { source: l.source, names: [], archivedOnly: true };
    entry.names.push(`${l.account_no || ''} ${l.account_name || ''}`.trim());
    if (!l.is_archived) entry.archivedOnly = false;
    bySource.set(l.source, entry);
  }
  const entries = [...bySource.values()];
  const shown = entries.slice(0, max);
  const extra = entries.length - shown.length;

  return (
    <span className="customer-link-badges">
      {shown.map((e) => {
        const cfg = LINK_SOURCES[e.source] || { icon: 'link', label: e.source };
        const who = e.names.length > 4
          ? `${e.names.slice(0, 4).join(', ')} and ${e.names.length - 4} more`
          : e.names.join(', ');
        const label = `Priced for ${who} (${cfg.label}${e.archivedOnly ? ', archived' : ''})`;
        return (
          <span
            key={e.source}
            className={`customer-link-badge is-${e.source}${e.archivedOnly ? ' is-archived' : ''}`}
            title={label}
            aria-label={label}
          >
            <span className="material-symbols-rounded">{cfg.icon}</span>
          </span>
        );
      })}
      {extra > 0 && <span className="customer-link-more">+{extra}</span>}
    </span>
  );
}

/**
 * The customer cell shared by every pricing table's single-row and
 * group-header row. Renders a 2×2 grid:
 *
 *   [acc#]    [name · status badges · fulfilment icon]
 *   [🔗 n]    [delivery address line 1]
 *
 * Row 1 wraps freely; the linked-PO badge sits under the account badge and the
 * address sits under the name.
 */
export function ContractCustomerCell({
  accountNo,
  accountId,
  onAccountClick,
  name,
  onNameClick,
  flags = {},
  status = null,
  manualFulfilled = false,
  linkedPoCount = 0,
  onLinkedClick,
  linkedScope,
  address,
  scopeKind,
}) {
  return (
    <div className="contract-customer-cell">
      <div className="ccc-acc">
        {accountNo && (
          <span
            className="account-badge-inline"
            style={accountId ? { cursor: 'pointer' } : undefined}
            onClick={accountId ? (e) => { e.stopPropagation(); if (onAccountClick) onAccountClick(e); } : undefined}
          >
            {accountNo}
          </span>
        )}
      </div>
      <div className="ccc-name">
        {name
          ? <span className="contact-link" onClick={(e) => { e.stopPropagation(); if (onNameClick) onNameClick(e); }}>{name}</span>
          : <span>-</span>}
        {flags.hold && <span className="hold-badge">HOLD</span>}
        {flags.draft && <span className="draft-badge">DRAFT</span>}
        {flags.archived && <span className="archived-badge">ARCHIVED</span>}
        <FulfilmentBadge status={status} manual={manualFulfilled} />
      </div>
      <div className="ccc-linked">
        <ScopeLinkBadge scopeKind={scopeKind} />
        <LinkedPosBadge count={linkedPoCount} onClick={onLinkedClick} scope={linkedScope} />
      </div>
      <div className="ccc-address">
        {address && <span className="delivery-address">{address}</span>}
      </div>
    </div>
  );
}

/**
 * "Group: Supreme Rearer 16 (bulk)" on a contract made from a product group
 * (contract.product_group from /api/pricing/contracts), with "+2 new in group"
 * when the group has gained plain products of the contract's pack since —
 * opening the contract offers to add them. Nothing for a contract not made
 * from a group.
 */
export function ContractGroupBadge({ group, compact = false }) {
  if (!group || !group.name) return null;
  const n = Number(group.new_count) || 0;
  const names = (group.new_members || []).map(m => `${m.item_code} ${m.item_name || ''}`.trim()).join('; ');
  return (
    <span className="contract-group-badges">
      <span className={`contract-group-badge${group.is_archived ? ' is-archived' : ''}`}
        title={`Made from the product group ${group.name} — its plain products in one pack are the products of this contract${group.is_archived ? '. The group has since been archived.' : ''}`}>
        <span className="material-symbols-rounded" aria-hidden="true">category</span>
        {compact ? group.name : groupLabel(group)}
      </span>
      {n > 0 && (
        <span className="contract-group-new"
          title={`${group.name} has gained ${n} plain product${n === 1 ? '' : 's'} since this contract was made: ${names}. Open the contract to add ${n === 1 ? 'it' : 'them'}.`}>
          +{n} new in group
        </span>
      )}
    </span>
  );
}
